import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { BannerType, BannerView } from "@dotnet/telematics-design-system";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import {
    DropdownValue,
    EditInlineDialogProps
} from "components/Actions/dialogs/types";
import Dialog from "components/DesignComponents/Dialog";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import ParamsContext from "context/params/paramsContext";
import { TRUE } from "shared/constants/strings";
import { isInternalTypeCompany } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useUserAccessLevels } from "shared/hooks/useUserAccessLevels";
import {
    ENDPOINTS,
    FILTER_NAMES,
    USER_ACCESS_LEVELS,
    USER_STATUSES
} from "shared/types/enums";
import { AccessGroup, Query } from "shared/types/interfaces";

import { AccessLevel, Role } from "../../components";
import AccessGroups from "./AccessGroups";
import UserStatus from "./UserStatus";

const EditUserInline = ({ data, isOpen, close }: EditInlineDialogProps) => {
    const { rootCompanyType } = useContext(ParamsContext);

    const { t } = useTranslation();

    const { updateData, handleResponse } = useApi();
    const { getUserAccessLevelInfo, isAllowedToAssignAccessLevel } =
        useUserAccessLevels();

    const isRootInternalTypeCompany = isInternalTypeCompany(rootCompanyType);

    const [isLoading, setIsLoading] = useState(false);
    const [roleResource, setRoleResource] = useState("");

    const [initialCompanyValue, setInitialCompanyValue] =
        useState<DropdownValue | null>(null);

    const [companyValue, setCompanyValue] = useState<DropdownValue | null>(
        null
    );

    const [hasCompanyChanged, setHasCompanyChanged] = useState(false);

    const [initialAccessGroups, setInitialAccessGroups] = useState<
        AccessGroup[]
    >([]);

    const [accessGroups, setAccessGroups] = useState<AccessGroup[]>([]);
    const [accessLevel, setAccessLevel] = useState<DropdownValue | null>(null);
    const [roles, setRoles] = useState<Query[]>([]);
    const [status, setStatus] = useState<USER_STATUSES | null>(null);
    const [expiresAt, setExpiresAt] = useState<string | null>(null);
    const [expiresAtPayload, setExpiresAtPayload] = useState<string | null>(
        null
    );
    const [hasExpiresAtError, setHasExpiresAtError] = useState(false);

    useEffect(() => {
        if (data) {
            const {
                company: { id: companyId, name: companyName },
                access_level,
                access_groups,
                roles,
                status,
                expires_at
            } = data;

            if (!isRootInternalTypeCompany) {
                setRoleResource(
                    `${ENDPOINTS.Roles}?${FILTER_NAMES.IncludeStatic}=${TRUE}&${FILTER_NAMES.CompanyId}=${companyId}`
                );
            }

            setInitialCompanyValue({
                id: companyId,
                name: companyName
            });

            setCompanyValue({
                id: companyId,
                name: companyName
            });

            const userAccessLevelData = getUserAccessLevelInfo(
                access_level as USER_ACCESS_LEVELS
            );

            setAccessLevel(userAccessLevelData);

            if (access_groups) {
                setInitialAccessGroups(access_groups);
                setAccessGroups(access_groups);
            }

            setRoles(roles);
            setStatus(status);
            status === USER_STATUSES.Active && setExpiresAt(expires_at);
            setExpiresAtPayload(expires_at);
        }

        // eslint-disable-next-line
    }, [data]);

    const handleCompanyValueChange = (newCompany: DropdownValue | null) => {
        setCompanyValue(newCompany);

        if (newCompany) {
            setHasCompanyChanged(newCompany.id !== initialCompanyValue?.id);
        }

        if (!isRootInternalTypeCompany) {
            setRoleResource(
                newCompany
                    ? `${ENDPOINTS.Roles}?${FILTER_NAMES.IncludeStatic}=${TRUE}&${FILTER_NAMES.CompanyId}=${newCompany.id}`
                    : ""
            );

            setRoles([]);
        }
    };

    const handleUserAccessLevelChange = (dropdownValue: DropdownValue) =>
        setAccessLevel(dropdownValue);

    const handleAccessGroupChange = (newAccessGroups: Query[]) =>
        setAccessGroups(newAccessGroups);

    const handleRoleChange = (newRoles: Query[]) => setRoles(newRoles);

    const handleStatusChange = (isChecked: boolean) => {
        const EXPIRED_DATE = "1970-01-01 00:00:00";

        const newStatus = isChecked
            ? USER_STATUSES.Active
            : USER_STATUSES.Inactive;

        setStatus(newStatus);
        setExpiresAt(null);
        setExpiresAtPayload(isChecked ? null : EXPIRED_DATE);
    };

    const handleExpirationDateChange = (
        newExpDate: string | null,
        newExpPayload: string | null
    ) => {
        setExpiresAt(newExpDate);
        setExpiresAtPayload(newExpPayload);
    };

    const handleExpirationDateError = (error: string | null) =>
        setHasExpiresAtError(error !== null);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const payload = {
                company_id: companyValue?.id,
                expires_at: expiresAtPayload,
                ...(isAllowedToAssignAccessLevel()
                    ? {
                          access_level: accessLevel?.id
                      }
                    : {}),
                ...(!isRootInternalTypeCompany
                    ? { role_ids: roles.map(role => role.id) }
                    : {}),
                ...(isInternalUser && !isSystemAndAboveUser
                    ? { access_group_ids: accessGroups.map(group => group.id) }
                    : {})
            };

            const response = await updateData(
                ENDPOINTS.Users,
                data.id,
                payload
            );

            close();
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const isReady = () => {
        if (!isRootInternalTypeCompany) {
            return !hasExpiresAtError && companyValue && roles.length > 0;
        }

        return (
            !hasExpiresAtError &&
            (isAllowedToAssignAccessLevel() || companyValue)
        );
    };

    const isNotPendingStatus =
        status === USER_STATUSES.Active || status === USER_STATUSES.Inactive;

    const isInternalUser = Boolean(
        accessLevel && accessLevel.id > USER_ACCESS_LEVELS.User
    );

    const isSystemAndAboveUser = Boolean(
        accessLevel && accessLevel.id >= USER_ACCESS_LEVELS.SystemUser
    );

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={t("General##edit user")}
            open={isOpen}
            close={close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setRoleResource("");
                        setInitialCompanyValue(null);
                        setCompanyValue(null);
                        setHasCompanyChanged(false);
                        setAccessLevel(null);
                        setInitialAccessGroups([]);
                        setAccessGroups([]);
                        setRoles([]);
                        setStatus(null);
                        setExpiresAt(null);
                        setExpiresAtPayload(null);
                        setHasExpiresAtError(false);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={isReady()}
                    cancelButtonTestId="cancel-edit-user-inline"
                    confirmationButtonTestId="save-edit-user-inline"
                />
            }
            extendToMaxWidth
        >
            <CompanyDropdown
                data-testid="edit-user-company-dropdown"
                value={companyValue}
                readOnly={isLoading}
                isRequired={!isAllowedToAssignAccessLevel()}
                isRootRequired
                change={(_, val) => handleCompanyValueChange(val)}
                customStyle={{ margin: "8px 0 16px 0" }}
            />

            {isInternalUser && hasCompanyChanged && (
                <BannerView
                    bannerType={BannerType.Info}
                    title={t("Dialog##access group inheritance")}
                    css={css({ marginBottom: "16px" })}
                />
            )}

            {isAllowedToAssignAccessLevel() && (
                <AccessLevel
                    disableClearable
                    isLoading={isLoading}
                    accessLevel={accessLevel}
                    onChange={handleUserAccessLevelChange}
                />
            )}

            {isInternalUser && (
                <AccessGroups
                    initialAccessGroups={initialAccessGroups}
                    selectedUserAccessGroups={accessGroups}
                    initialCompany={initialCompanyValue}
                    hasCompanyChanged={hasCompanyChanged}
                    isSubmitLoading={isLoading}
                    isDisabled={isSystemAndAboveUser}
                    onChange={handleAccessGroupChange}
                />
            )}

            <Role
                isLoading={isLoading}
                roleResource={roleResource}
                roles={roles}
                companyValue={companyValue}
                onChange={handleRoleChange}
            />

            {isNotPendingStatus && (
                <UserStatus
                    status={status}
                    expirationDate={expiresAt}
                    isLoading={isLoading}
                    onStatusChange={handleStatusChange}
                    onExpirationDateChange={handleExpirationDateChange}
                    onExpirationDateError={handleExpirationDateError}
                />
            )}
        </Dialog>
    );
};

export default EditUserInline;
